import React, { type ComponentPropsWithoutRef } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types';
import type { name_projectVersions_NameCell_jiraProject$key } from '@atlassian/jira-relay/src/__generated__/name_projectVersions_NameCell_jiraProject.graphql';
import type { name_projectVersions_NameCell_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/name_projectVersions_NameCell_jiraVersion.graphql';
import { softwareVersionDetailRoute } from '@atlassian/jira-router-routes-software-versions-routes';
import { Container } from '@atlassian/jira-version-modals/src/common/components/text-cell/styled';
import { Link } from '@atlassian/react-resource-router';
import { getVersionIssuesUrl } from './utils';

export type Props = {
	jiraVersion: name_projectVersions_NameCell_jiraVersion$key;
	jiraProject: name_projectVersions_NameCell_jiraProject$key;
	onClick: (arg1: AnalyticsEvent) => void;
};

const NameCell = ({ jiraVersion, jiraProject, onClick }: Props) => {
	const version = useFragment(
		graphql`
			fragment name_projectVersions_NameCell_jiraVersion on JiraVersion {
				versionId @required(action: THROW)
				name @required(action: THROW)
			}
		`,
		jiraVersion,
	);

	const project = useFragment(
		graphql`
			fragment name_projectVersions_NameCell_jiraProject on JiraProject {
				key @required(action: THROW)
				projectType @required(action: THROW)
			}
		`,
		jiraProject,
	);

	const href = getVersionIssuesUrl(
		project.key,
		project.projectType === 'SOFTWARE',
		version.versionId,
		version.name,
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onLinkClick = (...args: any[]) => {
		onClick(args[args.length - 1]);
	};

	return project.projectType === 'SOFTWARE' ? (
		<LinkWithAnalytics
			to={softwareVersionDetailRoute}
			params={{
				projectKey: project.key,
				versionId: version.versionId,
				tab: 'release-report-all-issues',
			}}
			prefetch="hover"
			data-testid="project-versions.ui.table.name.name"
			onClick={onLinkClick}
		>
			{version.name}
		</LinkWithAnalytics>
	) : (
		// This will point to a JQL search page for non-Software projects
		<LinkWithAnalytics
			href={href}
			data-testid="project-versions.ui.table.name.name"
			// @ts-expect-error - Type '(arg1: UIAnalyticsEvent) => void' is not assignable to type '(e: MouseEvent<Element, MouseEvent> | KeyboardEvent<Element>, args_1: UIAnalyticsEvent) => any'.
			onClick={onClick}
		>
			{version.name}
		</LinkWithAnalytics>
	);
};

export default withFireUiAnalytics({
	onClick: 'version',
})(NameCell);

const StyledLink = (props: ComponentPropsWithoutRef<typeof Link>) => (
	<Container>
		<StyledLinkComponent {...props} />
	</Container>
);

const LinkWithAnalytics = ComponentWithAnalytics('link', {
	onClick: 'clicked',
})(StyledLink);

// "display: block" so the link fits the full width of the cell, which...
// - Makes it easier to click.
// - Allows link prefetching to start earlier.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLinkComponent = styled(Link)({
	display: 'block',
});
